import { DAY_PASCAL } from '../../constants';
import mapDayToNumber from '../../util/mapDayToNumber';
import { ONE_DAY, WEEK_START } from './constants';
import createWeekEntry from './createWeekEntry';
import { Week } from './types';
import {
  endOfMonth,
  addDays,
  isSameMonth,
  differenceInDays,
  endOfWeek,
  getWeek,
  getDay
} from 'date-fns';

const SUNDAY = 0;

/**
 * Retrieves an array containing many weeks between the startDate and endDate
 * as well as whether a certain week is a split week.
 *
 * E.g. a split week is a week that falls between two months.
 * All weeks start on a Sunday
 *
 * @param startDate Start date of campaign
 * @param endDate End date of campaign
 * @returns Array containing all the week between the startDate and endDate.
 */
const getWeeksInRange = (
  startDate: Date,
  endDate: Date,
  commencingDay: DAY_PASCAL
): Week[] => {
  let currentDate = startDate;
  let weekCounter = WEEK_START;
  const weeks: Week[] = [];

  while (currentDate <= endDate) {
    const weekStart = currentDate;
    const weekEnd = getEndOfWeek(currentDate, endDate);

    if (isSameMonth(weekStart, weekEnd)) {
      createFullWeekEntry(
        weeks,
        weekStart,
        weekEnd,
        weekCounter,
        commencingDay
      );
    } else {
      createSplitWeekEntry(
        weeks,
        weekStart,
        weekEnd,
        weekCounter,
        commencingDay
      );
    }

    currentDate = shiftOneWeek(currentDate);
    weekCounter++;
  }

  return weeks;
};

const getEndOfWeek = (currentDate: Date, endDate: Date): Date => {
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: SUNDAY });

  if (weekEnd > endDate) {
    return endDate;
  }

  return weekEnd;
};

const shiftOneWeek = (weekStart: Date): Date => {
  return addDays(endOfWeek(weekStart), 1);
};

const createFullWeekEntry = (
  weeks: Week[],
  weekStart: Date,
  weekEnd: Date,
  week: number,
  commencingDay: DAY_PASCAL
): void => {
  const length = differenceInDays(weekEnd, weekStart) + 1;
  const commencingWeek =
    getDay(weekStart) !== SUNDAY
      ? weekStart
      : addDays(weekStart, mapDayToNumber(commencingDay));

  const newWeekEntry = createWeekEntry(
    weekStart,
    weekEnd,
    false,
    length,
    week,
    commencingDay,
    commencingWeek,
    getWeek(weekStart),
    undefined
  );
  weeks.push(newWeekEntry);
};

const createSplitWeekEntry = (
  weeks: Week[],
  weekStart: Date,
  weekEnd: Date,
  week: number,
  dayCommencing: DAY_PASCAL
): void => {
  const endOfFirstPart = endOfMonth(weekStart);
  const startOfSecondPart = addDays(endOfFirstPart, ONE_DAY);
  let month: number;

  const firstPartDiff = differenceInDays(endOfFirstPart, weekStart) + 1;
  const secondPartDiff = differenceInDays(weekEnd, startOfSecondPart) + 1;

  // the month of a given split week is which ever month has the most days.
  if (firstPartDiff > secondPartDiff) {
    month = endOfFirstPart.getMonth() + 1;
  } else {
    month = startOfSecondPart.getMonth() + 1;
  }

  const commencingWeek =
    getDay(weekStart) !== SUNDAY
      ? weekStart
      : addDays(weekStart, mapDayToNumber(dayCommencing));

  const newWeekEntry = createWeekEntry(
    weekStart,
    weekEnd,
    true,
    firstPartDiff,
    week,
    dayCommencing,
    commencingWeek,
    getWeek(weekStart),
    undefined,
    month
  );

  weeks.push(newWeekEntry);
};

export default getWeeksInRange;
